import React, { useMemo } from 'react';

import {
	AsaphAvatar,
	AsaphAvatarBackgroundTypes,
} from '@/components/shared/Avatar';
// import { Profile, UpdateProfile } from '../../../interfaces';
import {
	BottomActionLayout,
	BottomActionLayoutProps,
} from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { FormInput } from '@/components/shared/Inputs';
import { Blurb } from '@/components/shared/Inputs/Blurb';
import { Label } from '@/components/shared/Inputs/Label';
import { Select } from '@/components/shared/Inputs/Select';
import { ResponsiveBoxButtons } from '@/components/shared/ResponsiveBoxButtons';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAuth } from '@/providers/AuthProvider';
import { useTeam } from '@/providers/TeamProvider';
import { Formik, FormikErrors } from 'formik';
// import { S3Object } from 'models';
import * as Yup from 'yup';

import styles from './EditProfile.module.scss';

export interface EditProfileProps {
	onSave: () => void;
	onBack?: () => void;
	hasBack?: boolean;
	actionLabel?: string;
	bottomActionProps?: Partial<BottomActionLayoutProps>;
	leadingContent?: React.ReactElement;
}

type FormSchema = {
	displayName: string;
	gender?: string | null;
	birthYear?: number | null;
	color?: string | null;
};

const formValidationSchema: Yup.SchemaOf<FormSchema> = Yup.object().shape({
	displayName: Yup.string()
		.required('Required field')
		.min(1, 'Must be at least 1 character')
		.max(70, 'Name can be 70 characters long'),
	gender: Yup.string().nullable(),
	birthYear: Yup.number().nullable(),
	color: Yup.string().nullable(),
	// spotifyId: Yup.string().required('Please enter a valid url'),
});

export const EditProfile: React.FC<EditProfileProps> = ({
	onSave,
	onBack,
	hasBack = false,
	actionLabel = 'Save',
	bottomActionProps,
	leadingContent,
}) => {
	// return null;
	const { user, updateUser } = useAuth();
	const { refetch } = useTeam();
	const { handleError } = useErrorHandler();
	// const [touched, setTouched] = useState(false);

	const birthYearOptions = useMemo(() => {
		const MIN_AGE = 13; // App Store minimum age
		const maxYear = new Date().getFullYear() - MIN_AGE;
		const items = Array.from({ length: 100 }, (_, i) => {
			const year = maxYear - i;
			return { label: year.toString(), value: year };
		});
		return [...items, { label: 'Rather not say', value: null }];
	}, []);

	const initialValues: FormSchema = {
		displayName: user.displayName || '',
		gender: user.gender || null,
		birthYear: user.birthYear || null,
		color: user.color || null,
	};

	return (
		<Formik
			key="asaphSignUn"
			initialValues={initialValues}
			validationSchema={formValidationSchema}
			validateOnMount
			onSubmit={async (values: FormSchema) => {
				try {
					await updateUser({
						input: {
							displayName: values.displayName.trim() || '',
							gender: values.gender,
							birthYear: values.birthYear,
							color: values.color,
						},
					});
					await refetch();
					onSave();
				} catch (error) {
					handleError({
						errorCode: 'UpdateProfileError',
						friendlyMessage: 'Failed to update profile',
						error,
					});
					return;
				}
			}}
		>
			{({
				handleSubmit,
				handleChange,
				handleBlur,
				setFieldValue,
				values,
				errors,
				touched,
				isValid,
			}) => (
				<BottomActionLayout
					{...bottomActionProps}
					actionContent={
						<>
							{hasBack ? (
								<AsaphButton
									color="grey"
									label="Back"
									onClick={() => onBack?.()}
									fullWidth
								/>
							) : null}
							<AsaphButton
								color="blue"
								label={actionLabel}
								disabled={!isValid}
								onClick={() => handleSubmit()}
								fullWidth
							/>
						</>
					}
				>
					<form
						onSubmit={handleSubmit}
						id="asaphSignIn"
						className={styles['root']}
					>
						{leadingContent}
						<Label>Your name <span style={{ color: 'var(--px-color-error, #e53e3e)' }}>*</span></Label>
						<FormInput
							placeholder="Enter your name"
							name="displayName"
							id="displayName"
							autoComplete="off"
							value={values.displayName}
							onChange={(e) => {
								// setTouched(true);
								handleChange(e);
							}}
							onBlur={handleBlur}
							error={!!(touched.displayName && errors.displayName)}
						/>
						{touched.displayName && errors.displayName && (
							<Blurb isError>{errors.displayName}</Blurb>
						)}

						<div className="p-sm" />
						<Label
							tooltipHeading="How we use gender"
							tooltip="Asaph helps you find the sweet spot for every song. After each set, we gather song feedback on singing keys so you can discover what feels best for both men and women."
						>
							Gender
						</Label>
						<Select
							placeholder="Select gender"
							items={[
								{
									label: 'Male',
									value: 'male',
								},
								{
									label: 'Female',
									value: 'female',
								},
								{
									label: 'Rather not say',
									value: null,
								},
							]}
							onChange={(value) => {
								// setTouched(true);
								setFieldValue('gender', value);
							}}
							value={values.gender || ''}
						/>

						<div className="p-sm" />
						<Label
							tooltipHeading="How we use birth year"
							tooltip="Asaph helps you choose the best songs across generations. After each set, we map song feedback to age demographics so you can discover what resonates at any age."
						>
							Birth year
						</Label>
						<Select
							placeholder="Select year"
							items={birthYearOptions}
							onChange={(value) => {
								// setTouched(true);
								console.log(value);
								setFieldValue('birthYear', value);
							}}
							value={values.birthYear || ''}
						/>

						<div className="p-sm" />
						<Label>Avatar color</Label>
						<ResponsiveBoxButtons
							items={generateResponsiveBoxButtons(
								['yellow', 'green', 'teal'],
								setFieldValue,
								// setTouched,
								values.displayName,
								values.color,
							)}
						/>
						<ResponsiveBoxButtons
							items={generateResponsiveBoxButtons(
								['aqua', 'purple', 'maroon'],
								setFieldValue,
								// setTouched,
								values.displayName,
								values.color,
							)}
							className="mt-md"
						/>
						<ResponsiveBoxButtons
							items={generateResponsiveBoxButtons(
								['red', 'orange', 'blue'],
								setFieldValue,
								// setTouched,
								values.displayName,
								values.color,
							)}
							className="mt-md"
						/>
					</form>
				</BottomActionLayout>
			)}
		</Formik>
	);
};

const generateResponsiveBoxButtons = (
	colors: AsaphAvatarBackgroundTypes[],
	setFieldValue: (
		field: string,
		color: string,
	) => Promise<void | FormikErrors<FormSchema>>,
	// setTouched: React.Dispatch<React.SetStateAction<boolean>>,
	name: string,
	currentValue?: string | null,
) => {
	return colors.map((color) => ({
		content: (
			<AsaphAvatar size="small" name={name || 'A'} background={color} />
		),
		label: color.charAt(0).toUpperCase() + color.slice(1),
		selected: currentValue === color,
		onClick: async () => {
			await setFieldValue('color', color);
			// setTouched(true);
		},
	}));
};

export default EditProfile;
